import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Box, Card, CardContent, IconButton } from '@material-ui/core';
import { KeyboardDatePicker } from '@material-ui/pickers';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import moment from 'moment';
import { useHistory, useParams } from 'react-router-dom';
import { LayoutPageGrid } from 'ui/LayoutGrid/LayoutGrid';
import { useMeContext } from 'components';
import { usePermissionPage } from 'hooks';
import { PeriodsGrid, usePeriodsColumns, usePeriodsState } from './PeriodsGrid';
import { SendReportsButton } from './components/SendReportsButton';

type PeriodsPageParams = {
  year?: string;
  month?: string;
};

const buildMonth = (year?: string, month?: string) => {
  const currentMonth = moment().startOf('month');
  if (year && month) {
    const candidate = moment(`${year}-${month}-01`, 'YYYY-MM-DD', true);
    if (candidate.isValid() && !candidate.isAfter(currentMonth, 'month')) {
      return candidate.format('YYYY-MM-DD');
    }
  }
  return currentMonth.format('YYYY-MM-DD');
};

export const PeriodsPage = () => {
  const { user } = useMeContext();
  usePermissionPage('/wards', user?.permissions.wards.manageNeeds);

  const params = useParams<PeriodsPageParams>();
  const history = useHistory();

  const month = useMemo(() => buildMonth(params.year, params.month), [params.year, params.month]);

  useEffect(() => {
    if (!params.year || !params.month) return;
    const m = moment(month);
    if (params.year !== m.format('YYYY') || params.month !== m.format('MM')) {
      history.replace(`/periods/${m.format('YYYY')}/${m.format('MM')}`);
    }
  }, [params.year, params.month, month, history]);

  const navigateToMonth = useCallback(
    (value: string) => {
      const m = moment(value);
      if (!m.isValid()) return;
      history.push(`/periods/${m.format('YYYY')}/${m.format('MM')}`);
    },
    [history]
  );

  const refetchRef = useRef<(() => void) | undefined>(undefined);
  const refetchHandler = useCallback(() => refetchRef.current?.(), []);

  const columns = usePeriodsColumns();
  const state = usePeriodsState({ columns, rowsPerPage: 50, month });

  const isCurrentMonth = useMemo(() => moment(month).isSame(moment().startOf('month'), 'month'), [
    month,
  ]);

  const handlePrevMonth = useCallback(() => {
    navigateToMonth(moment(month).subtract(1, 'month').startOf('month').format('YYYY-MM-DD'));
  }, [month, navigateToMonth]);

  const handleNextMonth = useCallback(() => {
    navigateToMonth(moment(month).add(1, 'month').startOf('month').format('YYYY-MM-DD'));
  }, [month, navigateToMonth]);

  return (
    <LayoutPageGrid
      title="Периоды"
      iconButton={
        <Box ml={4} display="flex" alignItems="center">
          <IconButton size="small" aria-label="Предыдущий месяц" onClick={handlePrevMonth}>
            <ChevronLeftIcon />
          </IconButton>
          <Box mx={1}>
            <KeyboardDatePicker
              views={['year', 'month']}
              format="MMMM YYYY"
              value={moment(month)}
              onChange={(d) => {
                if (d?.isValid()) navigateToMonth(d.startOf('month').format('YYYY-MM-DD'));
              }}
              inputVariant="outlined"
              size="small"
            />
          </Box>
          {!isCurrentMonth && (
            <IconButton size="small" aria-label="Следующий месяц" onClick={handleNextMonth}>
              <ChevronRightIcon />
            </IconButton>
          )}
        </Box>
      }
      tools={<SendReportsButton month={month} onSent={refetchHandler} />}
    >
      <Card>
        <CardContent>
          <PeriodsGrid state={state} refetchRef={refetchRef} />
        </CardContent>
      </Card>
    </LayoutPageGrid>
  );
};
